import React from 'react';
import { Text } from 'src/modules/shared/components/atoms/text';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import TextInput from 'src/modules/shared/components/atoms/inputs/text-input';
import { PiPlus, PiTrash } from 'react-icons/pi';
import { FormFieldProps } from 'src/modules/shared/types/json-schema.type';
import { EmptySchemaInput } from 'src/modules/shared/components/atoms/json-schema-inputs/emtpy-schema-input';

/**
 * Props for ObjectField component
 */
export interface ObjectFieldProps extends FormFieldProps {
  value: Record<string, unknown>;
  title?: string;
}

export const ObjectField: React.FC<ObjectFieldProps> = ({
  fieldKey,
  schema,
  value = {},
  handleChange,
  disabled = false,
  allFieldsOptional = false,
  title,
  JSONSchemaForm,
}) => {
  if (schema.properties) {
    return (
      <div className="p-4 border rounded-md">
        {title && (
          <Text size="h6" className="mb-2">
            {title}
          </Text>
        )}
        <JSONSchemaForm
          schema={schema}
          formData={value || {}}
          onChange={(data: Record<string, unknown>) =>
            handleChange(fieldKey, data)
          }
          disabled={disabled}
          allFieldsOptional={allFieldsOptional}
        />
      </div>
    );
  }

  if (schema.additionalProperties) {
    const keyValuePairs = value ? Object.entries(value) : [];

    const handleAddProperty = () => {
      const newValue = { ...value };
      const existingKeys = Object.keys(newValue)
        .filter((key) => key.startsWith('new_key_'))
        .map((key) => {
          const match = key.match(/new_key_(\d+)/);
          return match ? parseInt(match[1], 10) : -1;
        });

      const highestIndex =
        existingKeys.length > 0 ? Math.max(...existingKeys) : -1;

      newValue[`new_key_${highestIndex + 1}`] = '';
      handleChange(fieldKey, newValue);
    };

    return (
      <div className="border rounded-md p-3 bg-gray-50">
        {keyValuePairs.map(([propKey, propValue], index) => (
          <div key={index} className="flex gap-2 mb-2">
            <TextInput
              value={propKey}
              onChange={(e) => {
                const newValue = { ...value };
                const oldValue = newValue[propKey];
                delete newValue[propKey];
                newValue[e.target.value] = oldValue;
                handleChange(fieldKey, newValue);
              }}
              // TODO: check when implementing i18n
              placeholder="Llave"
              className="flex-1"
            />
            <TextInput
              value={propValue as string}
              onChange={(e) => {
                const newValue = { ...value };
                newValue[propKey] = e.target.value;
                handleChange(fieldKey, newValue);
              }}
              // TODO: check when implementing i18n
              placeholder="Valor"
              className="flex-1"
            />
            <Button
              variant="danger"
              appearance="ghost"
              size="sm"
              type="button"
              onClick={() => {
                const newValue = { ...value };
                delete newValue[propKey];
                handleChange(fieldKey, newValue);
              }}
            >
              <PiTrash className="w-4 h-4" />
            </Button>
          </div>
        ))}

        <Button
          variant="primary"
          appearance="outline"
          size="sm"
          type="button"
          onClick={handleAddProperty}
          className="mt-2 flex items-center gap-1"
        >
          <PiPlus className="w-4 h-4" />
          <Text size="small" color="primary">
            {/* TODO: check when implementing i18n */}
            Agregar
          </Text>
        </Button>
      </div>
    );
  }

  return (
    <EmptySchemaInput
      id={`field-${fieldKey}`}
      value={value}
      onChange={(value) => handleChange(fieldKey, value)}
      disabled={disabled}
      schema={schema}
    />
  );
};
